/**
 * AvatarUploadCard — reusable Profile-photo block used by both
 * `MyProfile.jsx` (client) and `AdminSettings.jsx` (admin).
 *
 * Behaviour:
 *   • POST /api/users/me/avatar (multipart) on Upload — backend resizes,
 *     center-crops and re-encodes as webp. Response gives a versioned
 *     `avatar_url` so the cache busts automatically.
 *   • DELETE /api/users/me/avatar on Remove.
 *   • On success, calls `onChange(newAvatarUrl | null)` so the parent
 *     can reflect the change in its header / chat bubbles without a
 *     page reload.
 *   • Validation mirrors backend: jpg/png/webp, up to 5 MB. Anything
 *     larger or wrong-type is rejected client-side with a toast.
 *
 * Props
 *   token        — auth token
 *   avatarUrl    — currently saved URL (relative path), or null
 *   name         — display name used in the fallback initials avatar
 *   email        — fallback if `name` is empty
 *   onChange     — (newUrl | null) => void
 *   testidPrefix — optional override (`avatar-upload-card` by default)
 */
import React, { useRef, useState, useCallback } from 'react';
import { Camera, Trash2, Loader2, Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AuthedAvatarImage, { bustAuthedAvatarCache } from './AuthedAvatarImage';

const API_URL    = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES  = 5 * 1024 * 1024;
const ALLOWED    = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const getInitials = (name, email) => {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function AvatarUploadCard({
  token, avatarUrl, name, email, onChange,
  testidPrefix = 'avatar-upload-card',
  isAdmin = false,
}) {
  const { t } = useTranslation('profile');
  const tt = (key, en) => (isAdmin ? en : t(key, { defaultValue: en }));
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragHot, setDragHot] = useState(false);

  // The avatar GET endpoint requires `Authorization: Bearer …`, so a
  // plain `<img src="…">` would fail with 401 (the browser can't send
  // custom headers on img loads). We use AuthedAvatarImage to fetch the
  // blob with auth and render via an object URL instead.
  const initials = (
    <span>{getInitials(name, email)}</span>
  );

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!ALLOWED.includes((file.type || '').toLowerCase())) {
      toast.error('Only jpg, png or webp images are accepted.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 5 MB.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/users/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      // The new avatar URL carries a fresh `?v={hash}` so it's a new
      // cache key, but invalidate any older URL we may have shown
      // moments ago so a re-mount can't replay a stale blob.
      bustAuthedAvatarCache(avatarUrl);
      toast.success('Profile photo updated.');
      onChange?.(j.avatar_url);
    } catch (e) {
      toast.error(`Upload failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [token, onChange]);

  const remove = useCallback(async () => {
    if (!avatarUrl) return;
    if (!window.confirm('Remove your profile photo?')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/users/me/avatar`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      toast.success('Profile photo removed.');
      bustAuthedAvatarCache(avatarUrl);
      onChange?.(null);
    } catch (e) {
      toast.error(`Remove failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [token, avatarUrl, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragHot(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5"
      data-testid={testidPrefix}
    >
      <div className="flex items-start gap-5 flex-wrap">
        {/* Live preview / initials fallback */}
        <div className="relative flex-shrink-0">
          <div
            className={`w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg ring-2 ${avatarUrl ? 'ring-cyan-300' : 'ring-slate-200'} bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold`}
            data-testid={`${testidPrefix}-preview`}
          >
            {avatarUrl ? (
              <AuthedAvatarImage
                token={token}
                src={avatarUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                fallback={initials}
                testid={`${testidPrefix}-img`}
              />
            ) : (
              initials
            )}
          </div>
          {busy && (
            <div className="absolute inset-0 bg-white/70 rounded-full flex items-center justify-center"
                 data-testid={`${testidPrefix}-busy`}>
              <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
            </div>
          )}
        </div>

        {/* Drop zone + controls */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Camera className="w-4 h-4 text-cyan-600" />
            <h3 className="text-sm font-bold text-slate-800">{tt('avatar.title', 'Profile photo')}</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {tt('avatar.hint', 'jpg, png or webp · up to 5 MB. We crop to a square and downsize to 256×256.')}
          </p>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragHot(true); }}
            onDragLeave={() => setDragHot(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid={`${testidPrefix}-dropzone`}
            className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-3 text-xs text-center transition-colors ${
              dragHot
                ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-cyan-400 hover:text-cyan-700'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            {tt('avatar.drop_zone_prefix', 'Drop a photo here or')} <span className="underline">{tt('avatar.drop_zone_click', 'click to choose')}</span>
          </div>
          <input
            type="file"
            ref={inputRef}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testidPrefix}-input`}
          />

          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`${testidPrefix}-upload-btn`}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" /> {avatarUrl ? tt('avatar.replace', 'Replace photo') : tt('avatar.upload', 'Upload photo')}
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                data-testid={`${testidPrefix}-remove-btn`}
                className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> {tt('avatar.remove', 'Remove')}
              </button>
            )}
          </div>

          {!avatarUrl && (
            <p className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {tt('avatar.no_photo_hint', 'No photo set — your initials will show in chat instead.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
